import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { Printer } from 'lucide-react';
import { CrudList } from '../shared/CrudPage';
import { invoiceService, enumLabel } from '@kss/api';
import type { Invoice, InvoiceCreate, InvoiceFilter } from '@kss/api';
import { StatusBadge } from '@kss/ui';
import { useNameMap } from '../../hooks/useNameMaps';
import { encodeId } from '../../utils/idCodec';

export default function AllInvoices() {
  return (
    <CrudList<Invoice, InvoiceCreate, InvoiceFilter>
      title="All invoices" newLabel="New invoice" service={invoiceService}
      idKey="invoiceId" labelKey="invoiceNumber" basePath="/panel/invoices"
      // Newest invoice at the top — invoiceNumber embeds a date prefix
      // (INV-yyyyMMdd-…) that sorts wrong chronologically by default.
      // Ref: ORDER_WORKFLOW_AUDIT Issue 6.
      initialSortStatus={{ columnAccessor: 'invoiceDate', direction: 'desc' }}
      columns={[
        { accessor: 'invoiceNumber', title: 'Number', sortable: true, filterable: true, width: 140,
          /* Invoice number doubles as a deep link to the print-ready
             preview page — matches the format the user asked for
             (Print · Download PDF · WhatsApp share). */
          render: (r) => r.invoiceId ? (
            <Link
              to={`/panel/invoices/${encodeId(r.invoiceId)}/preview`}
              className="font-mono text-xs text-primary hover:underline"
            >
              {r.invoiceNumber ?? '—'}
            </Link>
          ) : <span className="font-mono text-xs">{r.invoiceNumber ?? '—'}</span>,
        },
        { accessor: 'invoiceDate', title: 'Date', sortable: true, render: (r) => r.invoiceDate ? new Date(r.invoiceDate).toLocaleDateString() : '—' },
        {
          accessor: 'customerId',
          title: 'Customer',
          /* SSO ISSUE 9 — panel-created invoices reference a real
             Customer row, so the id→name map SHOULD resolve; but
             storefront-checkout invoices stamp `customerId = user.id`
             and never mint a Customer row. Resolve through a layered
             fallback so the cell is never blank and never wrongly
             says "Guest" when the real name is available. See
             `CustomerCell` below for the priority chain. */
          render: (r) => <CustomerCell row={r} />,
        },
        { accessor: 'totalAmount', title: 'Total (₹)', sortable: true, textAlign: 'right', render: (r) => Number(r.totalAmount ?? 0).toLocaleString('en-IN') },
        { accessor: 'paidAmount', title: 'Paid (₹)', sortable: true, textAlign: 'right', render: (r) => Number(r.paidAmount ?? 0).toLocaleString('en-IN') },
        { accessor: 'status', title: 'Status', sortable: true,
          render: (r) => <StatusBadge status={enumLabel('InvoiceStatus', typeof r.status === 'number' ? r.status : Number(r.status))} /> },
        /* New "Print" action column — quick way to jump into the
           print-ready view without first opening the edit form. */
        { accessor: 'invoiceId', title: 'Print',
          render: (r) => r.invoiceId ? (
            <Link
              to={`/panel/invoices/${encodeId(r.invoiceId)}/preview`}
              title="Open print view"
              className="inline-flex h-7 w-7 items-center justify-center rounded text-content-secondary hover:bg-surface-card-hover hover:text-content-primary"
              onClick={(e) => e.stopPropagation()}
            >
              <Printer className="h-3.5 w-3.5" />
            </Link>
          ) : null,
        },
      ]}
    />
  );
}

/**
 * SSO ISSUE 9 — resolve an Invoice's customer display name.
 *
 * The previous version rendered `<NameCell kind="customer" fallback="Guest customer" />`
 * whenever the customer map missed, which surfaced "Guest customer" for
 * panel-created invoices too whenever the useNameMap fetch hadn't
 * populated the id yet (race on first render) or the invoice's
 * `customerId` was a `User.id` stamped by the storefront checkout.
 *
 * Priority chain (best → worst):
 *   1. Any denormalised `customerName` the backend ships inline.
 *   2. Nested `customer.name` — some list endpoints eager-load it.
 *   3. Real Customer map lookup via `useNameMap('customer')`.
 *   4. Billing/shipping address `fullName` parsed from *AddressJson.
 *   5. Muted "Guest customer" fallback so the cell is never blank.
 *
 * Rendering the fallback only AFTER the map finishes loading avoids the
 * transient "Guest customer" flash that fired before.
 */
function CustomerCell({ row }: { row: Invoice }): ReactElement {
  const map = useNameMap('customer');
  const rec = row as unknown as Record<string, unknown>;

  // 1. Denormalised customerName.
  const denorm = rec.customerName;
  if (typeof denorm === 'string' && denorm.trim()) {
    return <span className="text-sm">{denorm.trim()}</span>;
  }

  // 2. Nested customer object.
  const nested = rec.customer;
  if (nested && typeof nested === 'object') {
    const name = (nested as Record<string, unknown>).name;
    if (typeof name === 'string' && name.trim()) {
      return <span className="text-sm">{name.trim()}</span>;
    }
  }

  // 3. Customer map — the primary path for panel-created invoices.
  const cid = row.customerId ? String(row.customerId) : '';
  if (cid && map.byId[cid]) {
    return <span className="text-sm">{map.byId[cid]}</span>;
  }
  if (map.loading) {
    return <span className="text-xs italic text-content-muted">Loading…</span>;
  }

  // 4. Address fallback — storefront invoices always carry this.
  const addressRaw = rec.billingAddressJson ?? rec.shippingAddressJson;
  if (typeof addressRaw === 'string' && addressRaw.trim()) {
    try {
      const parsed = JSON.parse(addressRaw) as Record<string, unknown>;
      const full = parsed?.fullName ?? parsed?.name;
      if (typeof full === 'string' && full.trim()) {
        return <span className="text-sm">{full.trim()}</span>;
      }
    } catch {
      /* Not JSON — legacy rows may hold a plain string. */
    }
  }

  // 5. Final fallback.
  return <span className="text-sm text-content-muted">Guest customer</span>;
}
